import { Link } from 'react-router-dom';
import { useState } from 'react';

export default function HomePage() {
  const [formData, setFormData] = useState({
    name: '',
    age: '',
    phone: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const form = e.target as HTMLFormElement;
      const formDataToSend = new URLSearchParams();
      
      formDataToSend.append('name', formData.name);
      formDataToSend.append('age', formData.age);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d6me3vaqoe30lj0v8apg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSend.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', age: '', phone: '', email: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://public.readdy.ai/ai/img_res/fee2dc031e49e6987e8930c28c6ea518.jpg)'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/70 via-slate-900/50 to-slate-900/70"></div>
        
        <div className="relative z-10 max-w-5xl mx-auto px-6 text-center">
          <div className="mb-8">
            <img 
              src="https://public.readdy.ai/ai/img_res/54cbbdda-66bf-42bb-a6a9-309cdc1c15db.png" 
              alt="クリニックロゴ" 
              className="h-20 mx-auto mb-12"
            />
          </div>
          
          <h1 className="text-[40px] font-bold text-white mb-6 leading-tight">
            腰痛でスコアが伸び悩む
          </h1>
          
          <p className="text-2xl md:text-3xl text-amber-400 font-semibold mb-6">
            60代男性のあなたへ
          </p>
          
          <p className="text-xl md:text-2xl text-gray-200 mb-8 leading-relaxed">
            スコア90を切るための<br />あなたの知らない秘密の方法
          </p>
          
          <p className="text-sm text-gray-400 mb-12 max-w-2xl mx-auto">
            ※この方法はパターの技術を上げるワケでも、<br />飛距離を無理に伸ばすものでもありません
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a 
              href="#contact" 
              className="px-10 py-4 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-full text-lg transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              今すぐ予約する
            </a>
            <a 
              href="#solution" 
              className="px-10 py-4 border-2 border-white text-white font-bold rounded-full text-lg hover:bg-white/10 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              詳しく見る
            </a>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-24 bg-amber-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-8">
            <div className="flex gap-1">
              <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
              <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
              <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
            </div>
            <span className="text-sm font-bold text-orange-500 tracking-widest">PROBLEM</span>
          </div>
          
          <h2 className="text-5xl font-bold text-gray-900 mb-12 leading-tight">
            スイングを変えなくても<br />練習量を増やさなくても
          </h2>
          
          <div className="prose prose-lg max-w-3xl mb-16">
            <p className="text-gray-700 leading-relaxed text-lg mb-6">
              もし、腰痛が改善するだけで、自然とスコアが伸びるとしたら。
            </p>
            
            <p className="text-gray-700 leading-relaxed text-lg mb-6">
              当院に来られる60代の男性の多くは、最初こう話されます。
            </p>
            
            <div className="bg-white p-8 rounded-2xl shadow-sm mb-8 border-l-4 border-orange-500">
              <p className="text-xl font-bold text-gray-800 mb-4">「もう歳だから仕方ない」</p>
              <p className="text-xl font-bold text-gray-800 mb-4">「腰が痛いけど、ゴルフは続けたい」</p>
              <p className="text-xl font-bold text-gray-800">「スコアが伸びないのは努力不足だと思っていた」</p>
            </div>
            
            <p className="text-gray-700 leading-relaxed text-lg mb-6">
              しかし施術を重ねるうちに、変化が訪れます。
            </p>
            
            <p className="text-gray-700 leading-relaxed text-lg mb-6">
              腰の痛みが軽くなり、思い通りにクラブを振れるようになる。<br />
              無理に力を入れていないのに飛距離が伸び、<br />
              スイングに迷いがなくなり、気づけばスコアが改善しています。
            </p>
            
            <p className="text-gray-700 leading-relaxed text-lg mb-6">
              さらに、夜中の寝返りで感じていた腰の痛みが減り、<br />
              ぐっすり眠れるようになる方も少なくありません。<br />
              睡眠の質が上がることで、日常生活まで楽になるのです。
            </p>
            
            <p className="text-gray-700 leading-relaxed text-lg font-semibold">
              これは特別な才能の話ではありません。<br />
              多くの方が、<span className="text-orange-600">ある「体の使い方の誤解」</span>をしているだけなのです。
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="bg-white p-8 rounded-2xl shadow-sm text-center">
              <div className="text-5xl font-bold text-slate-800 mb-3">90%+</div>
              <div className="text-sm text-gray-600">の患者様が<br />効果を実感</div>
            </div>
            <div className="bg-white p-8 rounded-2xl shadow-sm text-center">
              <div className="text-5xl font-bold text-slate-800 mb-3">3回〜</div>
              <div className="text-sm text-gray-600">で変化を感じ始める<br />方が多数</div>
            </div>
            <div className="bg-white p-8 rounded-2xl shadow-sm text-center">
              <div className="text-5xl font-bold text-slate-800 mb-3">-12打</div>
              <div className="text-sm text-gray-600">平均スコア<br />改善幅</div>
            </div>
            <div className="bg-white p-8 rounded-2xl shadow-sm text-center">
              <div className="text-5xl font-bold text-slate-800 mb-3">60代</div>
              <div className="text-sm text-gray-600">専門の施術<br />プログラム</div>
            </div>
          </div>
        </div>
      </section>

      {/* Effort Section */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center leading-tight">
            スコアの伸び悩みは<br />あなたの努力不足ではないかもしれません
          </h2>
          
          <div className="prose prose-lg max-w-none">
            <p className="text-gray-700 leading-relaxed text-lg mb-6">
              真面目に練習してきました。<br />
              ゴルフ雑誌を読み、動画でフォームを研究し、<br />
              「次こそは90を切る」と本気で誓った。
            </p>
            
            <p className="text-gray-700 leading-relaxed text-lg mb-6">
              それなのに、スコアが変わらない。
            </p>
            
            <p className="text-gray-700 leading-relaxed text-lg mb-6">
              最近は腰の痛みのせいで思い切り振れず、<br />
              「昔より下手になった気がする」<br />
              そんな感覚を抱いていませんか。
            </p>
            
            <div className="bg-orange-50 p-10 rounded-2xl my-10 border-l-4 border-orange-500">
              <p className="text-2xl font-bold text-gray-900 mb-6">断言します。</p>
              <p className="text-lg text-gray-700 leading-relaxed">
                それはあなたの努力不足ではありません。
              </p>
            </div>
            
            <p className="text-gray-700 leading-relaxed text-lg mb-6">
              腰に痛みがあると、人は無意識に体をかばいます。<br />
              するとスイングは小さくなり、タイミングがズレ、<br />
              本来使える力が使えなくなります。
            </p>
            
            <p className="text-gray-700 leading-relaxed text-lg font-semibold">
              どれだけ練習しても結果が出にくい状態で、<br />
              努力を重ねているだけなのです。
            </p>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 bg-amber-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-center gap-3 mb-8">
            <div className="flex gap-1">
              <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
              <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
              <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
            </div>
            <span className="text-sm font-bold text-orange-500 tracking-widest">VOICE</span>
          </div>
          
          <h2 className="text-5xl font-bold text-gray-900 mb-6 text-center">患者さんの声</h2>
          <p className="text-xl text-gray-600 text-center mb-16">実際に施術を受けた60代ゴルファーの変化</p>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-md transition-shadow duration-300">
              <div className="flex gap-1 mb-4">
                {[1, 2, 3, 4, 5].map((star) => (
                  <i key={star} className="ri-star-fill text-orange-500 text-xl"></i>
                ))}
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">スイングが安定し、飛距離が伸びた</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                最初は半信半疑でした。腰が痛いのは年齢のせいだと思っていましたから。でも通ううちに、スイングが安定していくのを実感しました。無理に振っていないのに飛距離が伸びて、気づいたらスコアが10打も良くなっていました。
              </p>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center">
                  <i className="ri-user-line text-gray-500 text-xl"></i>
                </div>
                <div>
                  <div className="font-bold text-gray-900">田中様・62歳</div>
                  <div className="text-sm text-gray-500">会社員</div>
                </div>
              </div>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-md transition-shadow duration-300">
              <div className="flex gap-1 mb-4">
                {[1, 2, 3, 4, 5].map((star) => (
                  <i key={star} className="ri-star-fill text-orange-500 text-xl"></i>
                ))}
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">夜中の寝返りが楽になった</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                ゴルフのスコアだけでなく、日常生活も変わりました。夜中に寝返りを打つたびに目が覚めていたのが、今ではぐっすり眠れます。朝起きた時の腰の重さもなくなり、ゴルフがまた楽しくなりました。
              </p>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center">
                  <i className="ri-user-line text-gray-500 text-xl"></i>
                </div>
                <div>
                  <div className="font-bold text-gray-900">佐藤様・65歳</div>
                  <div className="text-sm text-gray-500">自営業</div>
                </div>
              </div>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-md transition-shadow duration-300">
              <div className="flex gap-1 mb-4">
                {[1, 2, 3, 4, 5].map((star) => (
                  <i key={star} className="ri-star-fill text-orange-500 text-xl"></i>
                ))}
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">思い切り振れる喜びを取り戻した</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                腰の不安がなくなって、思い切りクラブを振れるようになりました。以前は「痛くなったらどうしよう」という恐怖がありましたが、今は自信を持ってスイングできます。ゴルフ仲間にも「フォームが良くなった」と言われます。
              </p>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center">
                  <i className="ri-user-line text-gray-500 text-xl"></i>
                </div>
                <div>
                  <div className="font-bold text-gray-900">鈴木様・68歳</div>
                  <div className="text-sm text-gray-500">元会社役員</div>
                </div>
              </div>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-md transition-shadow duration-300">
              <div className="flex gap-1 mb-4">
                {[1, 2, 3, 4, 5].map((star) => (
                  <i key={star} className="ri-star-fill text-orange-500 text-xl"></i>
                ))}
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">3回目で変化を実感</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                正直、最初の1〜2回は半信半疑でした。でも3回目の施術後、明らかに腰の動きが違うことに気づきました。練習場で打った時、いつもより楽に振れて、ボールの飛び方も変わりました。今では月1回のメンテナンスで快適です。
              </p>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center">
                  <i className="ri-user-line text-gray-500 text-xl"></i>
                </div>
                <div>
                  <div className="font-bold text-gray-900">山田様・61歳</div>
                  <div className="text-sm text-gray-500">公務員</div>
                </div>
              </div>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-md transition-shadow duration-300">
              <div className="flex gap-1 mb-4">
                {[1, 2, 3, 4, 5].map((star) => (
                  <i key={star} className="ri-star-fill text-orange-500 text-xl"></i>
                ))}
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">ラウンド後の疲労感が激減</h3>
              <p className="text-gray-700 leading-relaxed mb-6">
                以前は18ホール回った後、腰が痛くて翌日は動けませんでした。今はラウンド後も疲れにくく、次の日も普通に生活できます。ゴルフの回数も増やせるようになり、仲間との時間がもっと楽しくなりました。
              </p>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center">
                  <i className="ri-user-line text-gray-500 text-xl"></i>
                </div>
                <div>
                  <div className="font-bold text-gray-900">高橋様・64歳</div>
                  <div className="text-sm text-gray-500">会社経営</div>
                </div>
              </div>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-md transition-shadow duration-300">
              <div className="flex gap-1 mb-4">
                {[1, 2, 3, 4, 5].map((star) => (
                  <i key={star} className="ri-star-fill text-orange-500 text-xl"></i>
                ))}
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">年齢を理由に諦めなくて良かった</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                「もう60代だから」と諦めかけていました。でもこちらで施術を受けて、まだまだ上達できることを実感しました。腰の痛みがなくなり、スコアも改善。ゴルフ人生がまた輝き始めた感じです。
              </p>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center">
                  <i className="ri-user-line text-gray-500 text-xl"></i>
                </div>
                <div>
                  <div className="font-bold text-gray-900">伊藤様・67歳</div>
                  <div className="text-sm text-gray-500">元教員</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Misconception Section */}
      <section id="solution" className="py-24 bg-slate-900">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 text-center leading-tight">
            あなたはスイングの際、<br />何か間違いはありませんか？
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <div className="bg-slate-800 p-10 rounded-2xl">
              <div className="w-16 h-16 bg-orange-500/20 rounded-full flex items-center justify-center mb-6">
                <i className="ri-close-circle-line text-orange-500 text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">腰を回している</h3>
              <p className="text-gray-300 leading-relaxed text-lg">
                多くの方が「腰を回す」ことを意識していますが、実は腰は回転のための場所ではありません。腰に負担が集中し、痛みの原因となります。
              </p>
            </div>

            <div className="bg-slate-800 p-10 rounded-2xl">
              <div className="w-16 h-16 bg-orange-500/20 rounded-full flex items-center justify-center mb-6">
                <i className="ri-close-circle-line text-orange-500 text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">脇を絞って体を固めている</h3>
              <p className="text-gray-300 leading-relaxed text-lg">
                熱心な人ほど、体を固めて安定させようとします。しかしこれが背骨の動きを制限し、スイングを小さくしてしまいます。
              </p>
            </div>
          </div>
          
          <p className="text-2xl text-white text-center font-semibold">
            実はこれ、すべて誤解です
          </p>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-24 bg-amber-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-8">
            <div className="flex gap-1">
              <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
              <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
              <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
            </div>
            <span className="text-sm font-bold text-orange-500 tracking-widest">SOLUTION</span>
          </div>
          
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-12 leading-tight">
            意外な真実<br />
            <span className="text-orange-600">腰は回らない。回るのは背骨の関節です</span>
          </h2>
          
          <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
            <div className="prose prose-lg">
              <p className="text-gray-700 leading-relaxed text-lg mb-6">
                <strong className="text-gray-900">腰は回転のための場所ではありません。</strong><br />
                体を支える部分です。
              </p>
              
              <p className="text-gray-700 leading-relaxed text-lg mb-6">
                スイング中に本来動くべきは、<br />
                <span className="text-orange-600 font-bold border-b-2 border-orange-600">背骨の関節</span>です。
              </p>
              
              <p className="text-gray-700 leading-relaxed text-lg mb-6">
                背骨は24個もの関節が連なり、<br />
                それぞれが少しずつ動くことで、<br />
                大きく、しなやかな回転動作を生み出します。
              </p>
              
              <p className="text-gray-700 leading-relaxed text-lg font-semibold">
                腰に痛みがあると、この背骨の動きが制限され、<br />
                体は代わりに腰を無理に回そうとしてしまう。<br />
                それが痛みとスコア停滞の原因です。
              </p>
            </div>
            
            <div className="relative">
              <img 
                src="https://readdy.ai/api/search-image?query=medical%20illustration%20of%20human%20spine%20vertebrae%20showing%20rotation%20movement%20with%20arrows%20indicating%20proper%20spinal%20joint%20motion%20during%20golf%20swing%20clean%20white%20background%20professional%20anatomical%20diagram%20educational%20style%20highlighting%20thoracic%20and%20lumbar%20spine%20segments&width=600&height=800&seq=spine001&orientation=portrait"
                alt="背骨の動きの説明図"
                className="w-full h-auto rounded-2xl shadow-lg"
              />
            </div>
          </div>
          
          <div className="bg-white p-10 rounded-2xl shadow-sm">
            <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">背骨の動きを改善した結果はこちら</h3>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-check-line text-white text-xl"></i>
                </div>
                <p className="text-gray-700 text-lg">腰の不安が減り、思い切り振れる</p>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-check-line text-white text-xl"></i>
                </div>
                <p className="text-gray-700 text-lg">スイングが自然に安定する</p>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-check-line text-white text-xl"></i>
                </div>
                <p className="text-gray-700 text-lg">飛距離が伸び、スコアが向上する</p>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-check-line text-white text-xl"></i>
                </div>
                <p className="text-gray-700 text-lg">寝返りが楽になる</p>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-check-line text-white text-xl"></i>
                </div>
                <p className="text-gray-700 text-lg">睡眠の質が上がる</p>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-check-line text-white text-xl"></i>
                </div>
                <p className="text-gray-700 text-lg">日常生活の動きも軽快になる</p>
              </div>
            </div>
            
            <p className="text-center text-gray-700 text-lg mt-10 font-semibold">
              特別なトレーニングやフォーム矯正は必要ありません。<br />
              土台を整えるだけです。
            </p>
          </div>
        </div>
      </section>

      {/* Flow Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-center gap-3 mb-8">
            <div className="flex gap-1">
              <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
              <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
              <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
            </div>
            <span className="text-sm font-bold text-orange-500 tracking-widest">FLOW</span>
          </div>
          
          <h2 className="text-5xl font-bold text-gray-900 mb-16 text-center">施術の流れ</h2>
          
          <div className="grid md:grid-cols-5 gap-6 mb-12">
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 text-center">
              <div className="w-16 h-16 bg-slate-900 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-6">
                ①
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">問診</h3>
              <p className="text-gray-600 leading-relaxed">
                腰の痛みやゴルフ中の違和感、生活状況を丁寧にお聞きします。
              </p>
            </div>

            <div className="hidden md:flex items-center justify-center">
              <i className="ri-arrow-right-line text-4xl text-gray-300"></i>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 text-center">
              <div className="w-16 h-16 bg-slate-900 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-6">
                ②
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">検査</h3>
              <p className="text-gray-600 leading-relaxed">
                背骨や関節の動きを確認し、原因を明確にします。
              </p>
            </div>

            <div className="hidden md:flex items-center justify-center">
              <i className="ri-arrow-right-line text-4xl text-gray-300"></i>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 text-center">
              <div className="w-16 h-16 bg-slate-900 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-6">
                ③
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">施術</h3>
              <p className="text-gray-600 leading-relaxed">
                体に負担をかけず、必要な部分だけをやさしく整えます。
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-5 gap-6">
            <div className="md:col-start-2 bg-white p-8 rounded-2xl shadow-sm border border-gray-100 text-center">
              <div className="w-16 h-16 bg-slate-900 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-6">
                ④
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">再検査</h3>
              <p className="text-gray-600 leading-relaxed">
                施術後の変化をその場で一緒に確認します。
              </p>
            </div>

            <div className="hidden md:flex items-center justify-center">
              <i className="ri-arrow-right-line text-4xl text-gray-300"></i>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 text-center">
              <div className="w-16 h-16 bg-slate-900 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-6">
                ⑤
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">会計・説明</h3>
              <p className="text-gray-600 leading-relaxed">
                無理な来院の提案や押し売りは一切ありません。
              </p>
            </div>
          </div>
          
          <p className="text-center text-orange-600 text-lg font-semibold mt-12">
            無理な勧誘は一切ありません
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section id="contact" className="relative py-32 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://readdy.ai/api/search-image?query=beautiful%20golf%20course%20at%20golden%20sunset%20hour%20with%20warm%20orange%20and%20amber%20tones%20peaceful%20serene%20atmosphere%20silhouette%20of%20golf%20flag%20on%20green%20distant%20fairways%20and%20trees%20professional%20landscape%20photography%20inspiring%20and%20hopeful%20mood&width=1920&height=1080&seq=cta001&orientation=landscape)'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/80 via-slate-900/70 to-slate-900/80"></div>
        
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <p className="text-lg font-bold text-white tracking-widest mb-6">期間限定のご案内</p>
          
          <h2 className="text-5xl md:text-6xl font-bold text-white mb-8 leading-tight">
            今すぐご予約ください
          </h2>
          
          <p className="text-xl text-gray-200 mb-8">
            一人ひとり丁寧に対応するため、<br />
            期間限定の特別枠をご用意しております。
          </p>
          
          <p className="text-2xl text-white mb-12">
            このご案内は<span className="text-orange-500 font-bold">【2月20日まで】</span>です。<br />
            枠が埋まり次第、受付を終了します。
          </p>
          
          <div className="bg-white/10 backdrop-blur-sm p-10 rounded-2xl mb-12 max-w-2xl mx-auto">
            <div className="space-y-4 text-left">
              <div className="flex items-center gap-4">
                <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-check-line text-white text-xl"></i>
                </div>
                <p className="text-white text-lg">腰を気にせず思い切り振りたい</p>
              </div>
              
              <div className="flex items-center gap-4">
                <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-check-line text-white text-xl"></i>
                </div>
                <p className="text-white text-lg">もう一度90を切るゴルフを楽しみたい</p>
              </div>
              
              <div className="flex items-center gap-4">
                <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-check-line text-white text-xl"></i>
                </div>
                <p className="text-white text-lg">年齢を理由に諦めたくない</p>
              </div>
            </div>
            
            <p className="text-white text-xl mt-8 font-semibold">
              そう思われたなら、今がタイミングです。
            </p>
          </div>
          
          <a 
            href="tel:09059201676"
            className="inline-flex items-center gap-4 px-12 py-6 bg-slate-900 hover:bg-slate-800 text-white font-bold rounded-full text-xl transition-all duration-300 shadow-2xl hover:shadow-3xl mb-8 whitespace-nowrap cursor-pointer"
          >
            <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center">
              <i className="ri-phone-line text-white text-2xl"></i>
            </div>
            <span>今すぐ予約する</span>
            <i className="ri-arrow-right-up-line text-2xl"></i>
          </a>
          
          <div className="text-white">
            <p className="text-3xl font-bold mb-3">📞 090-5920-1676</p>
            <p className="text-gray-300">「ゴルフの腰痛について相談したい」とお伝えください。</p>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-24 bg-white">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-gray-900 mb-6 text-center">お問い合わせフォーム</h2>
          <p className="text-gray-600 text-center mb-12">お電話が難しい方は、こちらのフォームからもご予約・ご相談いただけます。</p>
          
          <form 
            id="contact-form"
            data-readdy-form
            onSubmit={handleSubmit}
            className="bg-amber-50 p-10 rounded-2xl"
          >
            <div className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-gray-900 font-semibold mb-2">
                  お名前 <span className="text-orange-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-base"
                  placeholder="山田太郎"
                />
              </div>

              <div>
                <label htmlFor="age" className="block text-gray-900 font-semibold mb-2">
                  年齢 <span className="text-orange-500">*</span>
                </label>
                <input
                  type="text"
                  id="age"
                  name="age"
                  value={formData.age}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-base"
                  placeholder="60"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-gray-900 font-semibold mb-2">
                  電話番号 <span className="text-orange-500">*</span>
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-base"
                  placeholder="090-1234-5678"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-gray-900 font-semibold mb-2">
                  メールアドレス <span className="text-orange-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-base"
                  placeholder="example@email.com"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-gray-900 font-semibold mb-2">
                  ご相談内容
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  maxLength={500}
                  rows={5}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-base resize-none"
                  placeholder="腰痛の状態やゴルフのスコアについてお聞かせください（500文字以内）"
                />
                <p className="text-sm text-gray-500 mt-2">{formData.message.length}/500文字</p>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-8 py-4 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-lg text-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? '送信中...' : '予約・相談する'}
              </button>

              {submitStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 text-green-800 px-6 py-4 rounded-lg">
                  <p className="font-semibold">送信完了しました！</p>
                  <p className="text-sm mt-1">担当者より折り返しご連絡させていただきます。</p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 text-red-800 px-6 py-4 rounded-lg">
                  <p className="font-semibold">送信に失敗しました</p>
                  <p className="text-sm mt-1">お手数ですが、お電話にてお問い合わせください。</p>
                </div>
              )}
            </div>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-12 mb-12">
            <div>
              <img 
                src="https://public.readdy.ai/ai/img_res/54cbbdda-66bf-42bb-a6a9-309cdc1c15db.png" 
                alt="クリニックロゴ" 
                className="h-16 mb-6"
              />
              <p className="text-gray-400 leading-relaxed mb-6">
                60代ゴルファーの腰痛改善とスコア向上を専門とする施術院です。背骨の動きを整えることで、痛みのない快適なゴルフライフをサポートします。
              </p>
              <div className="flex gap-4">
                <a href="#" className="w-10 h-10 border border-white/30 rounded-full flex items-center justify-center hover:bg-white/10 transition-colors cursor-pointer">
                  <i className="ri-facebook-fill text-xl"></i>
                </a>
                <a href="#" className="w-10 h-10 border border-white/30 rounded-full flex items-center justify-center hover:bg-white/10 transition-colors cursor-pointer">
                  <i className="ri-twitter-x-line text-xl"></i>
                </a>
                <a href="#" className="w-10 h-10 border border-white/30 rounded-full flex items-center justify-center hover:bg-white/10 transition-colors cursor-pointer">
                  <i className="ri-instagram-line text-xl"></i>
                </a>
                <a href="#" className="w-10 h-10 border border-white/30 rounded-full flex items-center justify-center hover:bg-white/10 transition-colors cursor-pointer">
                  <i className="ri-line-fill text-xl"></i>
                </a>
              </div>
            </div>

            <div>
              <div className="mb-8">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">ADDRESS</p>
                <p className="text-white">東京都渋谷区〇〇1-2-3<br />〇〇ビル2F</p>
              </div>
              <div className="mb-8">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">PHONE</p>
                <p className="text-white text-xl font-bold">090-5920-1676</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">EMAIL</p>
                <p className="text-white">info@golf-clinic.jp</p>
              </div>
            </div>

            <div>
              <img 
                src="https://readdy.ai/api/search-image?query=modern%20clean%20medical%20clinic%20exterior%20building%20facade%20with%20professional%20signage%20daytime%20photography%20welcoming%20entrance%20contemporary%20architecture%20glass%20windows%20well%20maintained%20appearance&width=400&height=500&seq=clinic001&orientation=portrait"
                alt="クリニック外観"
                className="w-full h-64 object-cover rounded-lg"
              />
            </div>
          </div>

          <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-500 text-sm">© 2025 ゴルフ腰痛改善クリニック. All rights reserved.</p>
            <div className="flex gap-6 text-sm">
              <a href="#" className="text-gray-400 hover:text-white transition-colors cursor-pointer">プライバシーポリシー</a>
              <a href="#" className="text-gray-400 hover:text-white transition-colors cursor-pointer">利用規約</a>
              <a href="#" className="text-gray-400 hover:text-white transition-colors cursor-pointer">サイトマップ</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}